import { SidebarMenu, Spinner, Stack } from "@altimateai/lego";
import { Ref } from "react";
import ChatRowSkeleton from "./ChatRowSkeleton";
import { PlaygroundSidebarRow } from "./PlaygroundSidebarRow";
import { PlaygroundSession, SessionLifecycleListFilter } from "./types";

const SKELETON_COUNT = 10;

interface PlaygroundSidebarListProps {
  sessions: PlaygroundSession[];
  loading: boolean;
  error: unknown;
  searchText: string;
  listFilter: SessionLifecycleListFilter;
  isFetchingNextPage: boolean;
  observerRef: Ref<HTMLDivElement>;
  openSessionId: string | undefined;

  onArchive: (id: string) => void;
  onUnarchive: (id: string) => void;
  onDelete: (id: string) => void;
}

export const PlaygroundSidebarList = ({
  sessions,
  loading,
  error,
  searchText,
  listFilter,
  isFetchingNextPage,
  observerRef,
  openSessionId,
  onArchive,
  onUnarchive,
  onDelete,
}: PlaygroundSidebarListProps) => {
  if (loading) {
    return (
      <SidebarMenu>
        {Array.from({ length: SKELETON_COUNT }).map((_, idx) => (
          <ChatRowSkeleton key={idx} />
        ))}
      </SidebarMenu>
    );
  }

  if (error) {
    return (
      <div className="al-p-4 al-text-center al-text-red-500 al-text-sm">
        {error instanceof Error ? error.message : "Failed to load sessions"}
      </div>
    );
  }

  if (sessions.length === 0) {
    return (
      <div className="al-p-4 al-text-center al-text-muted-foreground al-text-sm">
        {searchText
          ? "No matching chats. Try a different keyword."
          : listFilter === "archived"
            ? "No archived chats."
            : "No chat sessions yet. Start a new conversation!"}
      </div>
    );
  }

  return (
    <>
      <SidebarMenu>
        {sessions.map(session => (
          <PlaygroundSidebarRow
            key={session.session_id}
            session={session}
            isActive={openSessionId === session.session_id}
            listFilter={listFilter}
            onArchive={onArchive}
            onUnarchive={onUnarchive}
            onDelete={onDelete}
          />
        ))}
      </SidebarMenu>
      <div ref={observerRef} className="al-h-4" />
      {isFetchingNextPage && (
        <Stack justify="center" className="al-py-4">
          <Spinner size="sm" aria-label="Loading more sessions" />
        </Stack>
      )}
    </>
  );
};
